<template>
  <!-- Only show when pod is running/starting, or has been activated by user -->
  <div v-if="isStarting || podIsRunning" class="pod-status-container">
    <div class="status-widget">
      <!-- Loading State -->
      <div v-if="isStarting" class="status-content loading">
        <div class="spinner"></div>
        <div class="status-text">
          <p class="title">KI wird gestartet...</p>
          <p class="subtitle">Dies kann eine Minute dauern</p>
        </div>
      </div>

      <!-- Ready State -->
      <div v-else-if="podIsRunning" class="status-content ready">
        <div class="status-icon">✓</div>
        <div class="status-text">
          <p class="title">KI ist bereit</p>
          <p class="subtitle">Sie können jetzt Bilder hochladen</p>
        </div>
        <button @click="handleStopPod" class="action-btn stop-btn">KI beenden</button>
      </div>

      <!-- Error State -->
      <div v-if="hasError" class="status-error">
        <p>Fehler beim Starten der KI</p>
        <button @click="retry" class="retry-btn">Erneut versuchen</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { useDialog } from '@vanjana/vue-ui';
  import { upscalerPod } from '../services/upscale.service.js';

  const isStarting = ref(false);
  const hasError = ref(false);
  const dialogService = useDialog();

  const emit = defineEmits<{
    (e: 'pod-stopped'): void;
  }>();

  // Reactive derived from upscalerPod.podStatus
  const podIsRunning = computed(() => upscalerPod.podStatus.value?.isRunning() || false);

  const statusMessage = computed(() => {
    if (isStarting.value) {
      return 'KI wird gestartet...';
    }
    if (podIsRunning.value) {
      return 'KI ist bereit';
    }
    return 'KI startet automatisch';
  });

  const checkPodStatus = async () => {
    try {
      await upscalerPod.getPodInfo();
      hasError.value = false;
    } catch (error) {
      console.error('[PodStatus] Error checking pod status:', error);
      hasError.value = true;
    }
  };

  const handleStopPod = async () => {
    const result = await dialogService.confirm({
      title: 'KI beenden?',
      message: 'Die KI wird heruntergefahren. Sie können sie jederzeit wieder starten.',
      confirmText: 'Ja, beenden',
      cancelText: 'Nein, weiterlaufen lassen',
    });

    if (result) {
      try {
        await upscalerPod.stopPod();
        // podStatus wird automatisch reaktiv aktualisiert durch getPodInfo
        emit('pod-stopped');
      } catch (error) {
        console.error('[PodStatus] Error stopping pod:', error);
        hasError.value = true;
      }
    }
  };

  const retry = async () => {
    hasError.value = false;
    await checkPodStatus();
  };

  onMounted(async () => {
    // Check once on mount
    await checkPodStatus();
  });

  defineExpose({
    isStarting,
    podIsRunning,
    checkPodStatus,
  });
</script>

<style scoped>
  .pod-status-container {
    position: fixed;
    bottom: var(--space-l, 32px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
  }

  .status-widget {
    background: var(--surface, #ffffff);
    border-radius: 12px;
    padding: var(--space-m, 16px) var(--space-l, 32px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 300px;
    display: flex;
    align-items: center;
    gap: var(--space-m, 16px);
  }

  .status-content {
    display: flex;
    align-items: center;
    gap: var(--space-m, 16px);
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid var(--color-border, #e0e0e0);
    border-top-color: var(--primary, #4f46e5);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .status-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
  }

  .status-content.ready .status-icon {
    color: var(--success, #10b981);
  }

  .status-content.idle .status-icon {
    color: var(--warning, #f59e0b);
  }

  .status-text {
    flex: 1;
  }

  .status-text p {
    margin: 0;
    font-size: 14px;
  }

  .status-text .title {
    font-weight: 500;
    color: var(--foreground, #000000);
  }

  .status-text .subtitle {
    font-size: 12px;
    color: var(--foreground-muted, #666666);
    margin-top: 2px;
  }

  .status-error {
    display: flex;
    align-items: center;
    gap: var(--space-m, 16px);
    color: var(--error, #ef4444);
    font-size: 14px;
  }

  .retry-btn {
    padding: 4px 12px;
    background: var(--error, #ef4444);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    transition: opacity 0.2s;
  }

  .retry-btn:hover {
    opacity: 0.8;
  }

  .action-btn {
    padding: 6px 12px;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .stop-btn {
    background: var(--error, #ef4444);
    color: white;
  }

  .stop-btn:hover {
    background: var(--error-dark, #dc2626);
    opacity: 0.9;
  }
</style>
